import useAuth from "hooks/useAuth";
import WebsiteLayout from "layouts/WebsiteLayout";
import React, {
  Suspense, Fragment, lazy, useEffect,
} from "react";
import { Routes, Route } from "react-router-dom";
import PrivateRoute from "routes/PrivateRoute";
import NotFound from "views/errors/notFound";

const RoutesRenderer = ({ routes: routeList = [] }) => {
  const { isLoggedIn, user } = useAuth();

  useEffect(() => {
    if (!window.location.pathname.split("/").includes("account") && user?.email && !user?.dob) {
      window.location.href = "/account?redirect=start";
    }
  }, [user]);

  return (
    <Suspense fallback={<div />}>
      <Routes>
        {routeList.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const showHeader = route?.showHeader;
          const showFooter = route?.showFooter;
          const layoutProps = route.layout ? { showHeader, showFooter } : {};

          let Element = route.element;

          if (route.isPrivate && !isLoggedIn()) {
            Element = NotFound;
          }

          return (
            <Route
              key={route.path || i}
              path={route.path}
              element={(
                <Guard>
                  <Layout {...layoutProps}>
                    {route.isPrivate && route.routes && (
                      <PrivateRoute>
                        <RoutesRenderer routes={route.routes} />
                      </PrivateRoute>
                    )}
                    {route.isPrivate && !route.routes && (
                      <PrivateRoute>
                        <Element />
                      </PrivateRoute>
                    )}
                    {!route.isPrivate && route.routes && (
                      <RoutesRenderer routes={route.routes} />
                    )}
                    {!route.isPrivate && !route.routes && <Element />}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Routes>
    </Suspense>
  );
};

export const renderRoutes = (routesList = []) => <RoutesRenderer routes={routesList} />;

const routes = [
  {
    exact: "true",
    path: "/signin",
    element: lazy(() => import("views/auth/signIn")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/signup",
    element: lazy(() => import("views/auth/signUp")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/account",
    element: lazy(() => import("views/auth/user")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/edit-info",
    element: lazy(() => import("views/edit-info")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/start",
    element: lazy(() => import("views/start")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/fortune",
    element: lazy(() => import("views/fortune")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/fortune-by-year",
    element: lazy(() => import("views/fortune-by-year")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/fortune-by-year/cards",
    element: lazy(() => import("views/fortune-by-year/cards")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/form",
    element: lazy(() => import("views/total-fortune/form")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/result",
    element: lazy(() => import("views/total-fortune/free-report")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/basic/result/:id",
    element: lazy(() => import("views/total-fortune/basic-report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/premium/intro",
    element: lazy(() => import("views/total-fortune/premium/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/premium/form/:id",
    element: lazy(() => import("views/total-fortune/premium/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/premium/result/:id",
    element: lazy(() => import("views/total-fortune/premium/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/money/intro",
    element: lazy(() => import("views/total-fortune/deep-analysis/money/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/money/:id/form",
    element: lazy(() => import("views/total-fortune/deep-analysis/money/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/money/:id/report",
    element: lazy(() => import("views/total-fortune/deep-analysis/money/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/career/intro",
    element: lazy(() => import("views/total-fortune/deep-analysis/career/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/career/:id/form",
    element: lazy(() => import("views/total-fortune/deep-analysis/career/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/career/:id/report",
    element: lazy(() => import("views/total-fortune/deep-analysis/career/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/love/intro",
    element: lazy(() => import("views/total-fortune/deep-analysis/love/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/love/:id/form",
    element: lazy(() => import("views/total-fortune/deep-analysis/love/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/love/:id/report",
    element: lazy(() => import("views/total-fortune/deep-analysis/love/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/match/intro",
    element: lazy(() => import("views/total-fortune/deep-analysis/match/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/match/:id/form",
    element: lazy(() => import("views/total-fortune/deep-analysis/match/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/match/:id/report",
    element: lazy(() => import("views/total-fortune/deep-analysis/match/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/caution/intro",
    element: lazy(() => import("views/total-fortune/deep-analysis/caution/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/caution/:id/form",
    element: lazy(() => import("views/total-fortune/deep-analysis/caution/form")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/total-fortune/deep-analysis/caution/:id/report",
    element: lazy(() => import("views/total-fortune/deep-analysis/caution/report")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune",
    element: lazy(() => import("views/daily-fortune")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune/cards",
    element: lazy(() => import("views/daily-fortune/cards")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune/:id",
    element: lazy(() => import("views/daily-fortune/premium-all")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune/date-selection/:id",
    element: lazy(() => import("views/daily-fortune/date-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune/cards/:id",
    element: lazy(() => import("views/daily-fortune/premium-single-category")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/daily-fortune/premium-tests",
    element: lazy(() => import("views/daily-fortune/premium-tests")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/marriage-fortune/premium-tests",
    element: lazy(() => import("views/marriage-fortune/premium-tests")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/marriage-fortune/:id",
    element: lazy(() => import("views/marriage-fortune/details")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/marriage-fortune/months-selection/:id",
    element: lazy(() => import("views/marriage-fortune/months-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-fortune/intro",
    element: lazy(() => import("views/real-estate-fortune/intro")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/real-estate-fortune/:id",
    element: lazy(() => import("views/real-estate-fortune")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-fortune/situation-selection/:id",
    element: lazy(() => import("views/real-estate-fortune/situation-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-fortune/address-selection/:type/:id",
    element: lazy(() => import("views/real-estate-fortune/address-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-fortune/details/:id",
    element: lazy(() => import("views/real-estate-fortune/details")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-business-fortune/intro",
    element: lazy(() => import("views/real-estate-business-fit/intro")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-business-fortune/:id",
    element: lazy(() => import("views/real-estate-business-fit")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-business-fortune/business-selection/:id",
    element: lazy(() => import("views/real-estate-business-fit/business-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-business-fortune/address-selection/:type/:id",
    element: lazy(() => import("views/real-estate-business-fit/address-selection")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/real-estate-business-fortune/details/:id",
    element: lazy(() => import("views/real-estate-business-fit/details")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/lotto-draw",
    element: lazy(() => import("views/lotto-draw")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/lotto-draw/numbers",
    element: lazy(() => import("views/lotto-draw/numbers")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/lotto-draw/end",
    element: lazy(() => import("views/lotto-draw/end")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/name-fortune",
    element: lazy(() => import("views/name-fortune/index")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/name-fortune/numbers",
    element: lazy(() => import("views/name-fortune/numbers")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/name-fortune/result",
    element: lazy(() => import("views/name-fortune/result")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/name-fortune/numbers/:id",
    element: lazy(() => import("views/name-fortune/premium-numbers")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/name-fortune/result/:id",
    element: lazy(() => import("views/name-fortune/premium-result")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/fortune/cards",
    element: lazy(() => import("views/fortune/cards")),
    isPrivate: true,
  },
  {
    exact: "true",
    path: "/test",
    element: lazy(() => import("views/test")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    exact: "true",
    path: "/",
    element: lazy(() => import("views/test")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    exact: "true",
    path: "/mypage",
    element: lazy(() => import("views/mypage")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    exact: "true",
    path: "/not-found",
    element: lazy(() => import("views/errors/notFound")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    path: "*",
    element: lazy(() => import("views/errors/notFound")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    path: "/payments",
    element: lazy(() => import("views/payments")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    path: "/payments/success",
    element: lazy(() => import("views/payments/success")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    path: "/payments/failure",
    element: lazy(() => import("views/payments/failure")),
    isPrivate: false,
    layout: WebsiteLayout,
  },
  {
    path: "/top-ups",
    element: lazy(() => import("views/top-ups/index")),
    isPrivate: false,
    layout: WebsiteLayout,
    showHeader: false,
    showFooter: false,
  },
  {
    path: "/points-history",
    element: lazy(() => import("views/points-history/index")),
    isPrivate: true,
  },
  {
    path: "/premium-reports",
    element: lazy(() => import("views/premium-reports/index")),
    isPrivate: true,
  },
  {
    path: "/name-fortune/premium-reports/intro",
    element: lazy(() => import("views/name-fortune/intro")),
    isPrivate: false,
  },
  {
    path: "/my-destiny-number",
    element: lazy(() => import("views/my-destiny-number/index")),
    isPrivate: true,
  },
  {
    path: "/contact-us",
    element: lazy(() => import("views/contact-us/index")),
    isPrivate: false,
  },
  {
    exact: "true",
    path: "/landing/total-fortune",
    element: lazy(() => import("views/landing-total-fortune")),
    isPrivate: false,
    layout: WebsiteLayout,
    showHeader: false,
    showFooter: false,
  },
  {
    exact: "true",
    path: "/landing/today-fortune",
    element: lazy(() => import("views/landing-today-fortune")),
    isPrivate: false,
    layout: WebsiteLayout,
    showHeader: false,
    showFooter: false,
  },
  {
    exact: "true",
    path: "/landing/marriage-date",
    element: lazy(() => import("views/landing-marriage-date")),
    isPrivate: false,
    layout: WebsiteLayout,
    showHeader: false,
    showFooter: false,
  },
];

export default routes;
